import React from 'react';

export default function TimingHeatmap({ metrics }) {
  const { byDayOfWeek, byTimeBucket, journeyPosition } = metrics || {};

  // Find max count for scaling
  const maxDayCount = Math.max(...(byDayOfWeek?.map(d => d.count) || [1]));
  const maxTimeCount = Math.max(...(byTimeBucket?.map(t => t.count) || [1]));

  const getIntensity = (count, max) => {
    if (max === 0) return { bg: 'bg-gray-100', text: 'text-gray-700' };
    const ratio = count / max;
    if (ratio === 0) return { bg: 'bg-gray-100', text: 'text-gray-700' };
    if (ratio < 0.25) return { bg: 'bg-primary/20', text: 'text-gray-700' };
    if (ratio < 0.5) return { bg: 'bg-primary/40', text: 'text-white' };
    if (ratio < 0.75) return { bg: 'bg-primary/60', text: 'text-white' };
    return { bg: 'bg-primary/80', text: 'text-white' };
  };

  const timeBucketLabels = {
    morning: 'Morning (6am-12pm)',
    afternoon: 'Afternoon (12-5pm)',
    evening: 'Evening (5-9pm)',
    night: 'Night (9pm-6am)'
  };

  const journeyTotal = (journeyPosition?.first || 0) + (journeyPosition?.middle || 0) +
    (journeyPosition?.last || 0) + (journeyPosition?.only || 0);

  const getJourneyPercent = (value) => {
    if (journeyTotal === 0) return 0;
    return Math.round((value / journeyTotal) * 100);
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h3 className="text-sm font-medium text-gray-500 mb-4">Timing Analytics</h3>

      <div className="space-y-6">
        {/* Day of Week */}
        <div>
          <p className="text-xs font-medium text-gray-700 mb-2">Day of Week</p>
          <div className="flex gap-1">
            {byDayOfWeek?.map((day) => {
              const intensity = getIntensity(day.count, maxDayCount);
              return (
                <div key={day.day} className="flex-1 text-center">
                  <div
                    className={`h-8 rounded ${intensity.bg} flex items-center justify-center`}
                    title={`${day.day}: ${day.count} check-ins`}
                  >
                    <span className={`text-xs font-medium ${intensity.text}`}>{day.count}</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">{day.day}</p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Time of Day */}
        <div>
          <p className="text-xs font-medium text-gray-700 mb-2">Time of Day</p>
          <div className="space-y-1">
            {byTimeBucket?.map((time) => (
              <div key={time.bucket} className="flex items-center gap-2">
                <span className="text-xs text-gray-500 w-32">{timeBucketLabels[time.bucket]}</span>
                <div className="flex-1 bg-gray-100 rounded-full h-4 overflow-hidden">
                  <div
                    className="bg-primary h-full rounded-full transition-all"
                    style={{ width: maxTimeCount ? `${(time.count / maxTimeCount) * 100}%` : '0%' }}
                  />
                </div>
                <span className="text-xs text-gray-600 w-8 text-right">{time.count}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Journey Position */}
        <div>
          <p className="text-xs font-medium text-gray-700 mb-2">Journey Position</p>
          <p className="text-xs text-gray-500 mb-2">When do visitors typically come to your listing?</p>
          <div className="grid grid-cols-4 gap-2">
            <div className="text-center p-2 rounded bg-gray-50">
              <p className="text-lg font-semibold text-gray-900">{getJourneyPercent(journeyPosition?.first)}%</p>
              <p className="text-xs text-gray-500">1st Stop</p>
            </div>
            <div className="text-center p-2 rounded bg-gray-50">
              <p className="text-lg font-semibold text-gray-900">{getJourneyPercent(journeyPosition?.middle)}%</p>
              <p className="text-xs text-gray-500">Middle</p>
            </div>
            <div className="text-center p-2 rounded bg-gray-50">
              <p className="text-lg font-semibold text-gray-900">{getJourneyPercent(journeyPosition?.last)}%</p>
              <p className="text-xs text-gray-500">Last Stop</p>
            </div>
            <div className="text-center p-2 rounded bg-gray-50">
              <p className="text-lg font-semibold text-gray-900">{getJourneyPercent(journeyPosition?.only)}%</p>
              <p className="text-xs text-gray-500">Only Stop</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
